import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import TrovaBrand from '../components/Auth/TrovaBrand';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to send reset email');
      }

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <TrovaBrand />
            <h2 className="login-title">Check Your Email</h2>
            <p className="login-subtitle">
              If an account exists with that email address, we've sent you a link to reset your password.
            </p>
          </div>

          <div className="login-form-container">
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <svg
                style={{ width: '64px', height: '64px', color: 'var(--color-primary)', margin: '0 auto 16px' }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <p style={{ color: 'var(--color-text-tertiary)', marginBottom: '24px' }}>
                Please check your inbox and follow the instructions in the email to reset your password.
              </p>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px', marginBottom: '24px' }}>
                The link will expire in 1 hour.
              </p>
            </div>

            <Link
              to="/auth/login"
              className="form-button"
              style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <Helmet>
        <title>Forgot Password</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="login-container">
        <div className="login-header">
          <TrovaBrand />
          <h2 className="login-title">Forgot Password</h2>
          <p className="login-subtitle">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        <div className="login-form-container">
          {error && (
            <div
              style={{
                backgroundColor: 'var(--color-error-bg)',
                border: '1px solid var(--color-error)',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '16px',
                color: 'var(--color-danger)',
                fontSize: '14px',
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>

            <button type="submit" className="form-button" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>

          <div className="signup-prompt" style={{ marginTop: '24px' }}>
            Remember your password? <Link to="/auth/login">Back to Login</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
